import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ShieldCheck, MapPin, ArrowRight, Quote, Sparkles } from "lucide-react";
import { ForceLogo } from "../ForceLogo";
import AdSpot from "../AdSpot";
import ReferralPromo from "../ReferralPromo";
import HeroVideo from "../HeroVideo";
import ReferralLeaderboard from "../ReferralLeaderboard";
import { useLandingData } from "../../hooks/useLandingData";
import { mediaUrl } from "../../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1631396326838-de37e5f8bcbc?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80&ixlib=rb-4.1.0";

const TAGLINES = [
    "Let the FORCE assist you.",
    "Trusted hands for every corner of your home.",
    "Post it today — consider it handled.",
];

const TRADES = [
    { name: "Carpentry", img: "https://images.unsplash.com/photo-1631396326838-de37e5f8bcbc?crop=entropy&cs=srgb&fm=jpg&w=700&q=80&ixlib=rb-4.1.0" },
    { name: "Renovation", img: "https://images.unsplash.com/photo-1618832515490-e181c4794a45?crop=entropy&cs=srgb&fm=jpg&w=700&q=80&ixlib=rb-4.1.0" },
    { name: "Tiling", img: "https://images.unsplash.com/photo-1731168273756-e02cae42265b?crop=entropy&cs=srgb&fm=jpg&w=700&q=80&ixlib=rb-4.1.0" },
    { name: "Painting", img: "https://images.unsplash.com/photo-1595814433015-e6f5ce69614e?crop=entropy&cs=srgb&fm=jpg&w=700&q=80&ixlib=rb-4.1.0" },
    { name: "Cleaning", img: "https://images.unsplash.com/photo-1607400201515-c2c41c07d307?crop=entropy&cs=srgb&fm=jpg&w=700&q=80&ixlib=rb-4.1.0" },
    { name: "Yard Work", img: "https://images.unsplash.com/photo-1505798577917-a65157d3320a?crop=entropy&cs=srgb&fm=jpg&w=700&q=80&ixlib=rb-4.1.0" },
];

const STEPS = [
    { n: "01", title: "Post your job", body: "Describe what needs doing, set a fair budget, and drop a pin on your neighbourhood. Local pros take it from there." },
    { n: "02", title: "Meet local pros", body: "Background-checked people nearby respond with honest quotes and reviews you can trust." },
    { n: "03", title: "Sit back & relax", body: "Payment stays safe until the job's done right. Approve, release, and let the FORCE assist you." },
];

function Reveal({ children, delay = 0, y = 40 }) {
    return (
        <motion.div initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1], delay }} className="h-full">
            {children}
        </motion.div>
    );
}

// LAYOUT 3 — "Homestyle Editorial": a warm, magazine-style home-services showcase.
// Keeps the {header, rightSlot} contract — the right slot is a floating glass
// account panel holding the guest auth card OR the live jobs map.
export default function Layout3Editorial({ header, rightSlot }) {
    const { jobsToday, hiredToday, events, pros } = useLandingData();
    const [ti, setTi] = useState(0);

    useEffect(() => {
        const lenis = new Lenis({ lerp: 0.08, smoothWheel: true });
        let raf; const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
        raf = requestAnimationFrame(loop);
        return () => { cancelAnimationFrame(raf); lenis.destroy(); };
    }, []);

    useEffect(() => {
        const id = setInterval(() => setTi((i) => (i + 1) % TAGLINES.length), 3600);
        return () => clearInterval(id);
    }, []);

    return (
        <div className="relative min-h-screen bg-force-bg text-white" data-testid="layout-editorial">
            {header}

            {/* 1 + 2 — Atmospheric hero with floating account panel (rightSlot) */}
            <section className="relative mx-auto max-w-7xl px-5 sm:px-10 pt-6 md:pt-10 pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 lg:items-center">
                    {/* Left — editorial copy over a warm craft image */}
                    <div className="lg:col-span-7 relative">
                        <div className="media-overlay relative overflow-hidden rounded-[2rem] border border-orange-500/20 shadow-2xl">
                            <img src={HERO_IMG} alt="Local pro at work" className="h-[380px] sm:h-[460px] w-full object-cover kenburns" />
                            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/10" />
                            <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-10">
                                <span className="inline-flex w-fit items-center gap-2 rounded-full border border-orange-400/40 bg-zinc-950/60 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] text-orange-300">
                                    <Sparkles className="h-3.5 w-3.5" /> Local · Trusted · Rated
                                </span>
                                <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[0.92] tracking-tight text-white">
                                    Let the <span className="text-orange-500">FORCE</span><br />assist you.
                                </h1>
                                <div className="mt-4 h-6 overflow-hidden">
                                    <AnimatePresence mode="wait">
                                        <motion.p key={ti} initial={{ y: 22, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -22, opacity: 0 }} transition={{ duration: 0.5 }} className="text-sm sm:text-base italic text-zinc-300" data-testid="editorial-tagline">
                                            {TAGLINES[ti]}
                                        </motion.p>
                                    </AnimatePresence>
                                </div>
                            </div>
                        </div>
                        {/* Impact stat strip below hero */}
                        <div className="mt-4 grid grid-cols-2 gap-3">
                            <div className="rounded-2xl border border-white/10 bg-zinc-950/50 px-5 py-4" data-testid="editorial-jobs-today">
                                <p className="font-display text-3xl font-black text-orange-500">{jobsToday ?? "—"}</p>
                                <p className="mt-0.5 text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">Jobs posted today</p>
                            </div>
                            <div className="rounded-2xl border border-white/10 bg-zinc-950/50 px-5 py-4" data-testid="editorial-hired-today">
                                <p className="font-display text-3xl font-black text-orange-500">{hiredToday ?? "—"}</p>
                                <p className="mt-0.5 text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">Hired near you today</p>
                            </div>
                        </div>
                    </div>

                    {/* Right — floating account panel (auth OR map) */}
                    <div className="lg:col-span-5 lg:-mt-16 relative z-10">
                        <div className="relative rounded-[1.75rem] border border-orange-500/25 bg-zinc-950/50 p-3 sm:p-4 backdrop-blur-2xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8),0_0_50px_-18px_rgba(255,122,0,0.45)]">
                            <div className="pointer-events-none absolute inset-[6px] rounded-[1.4rem] border border-white/10" />
                            <div className="mb-2.5 flex items-center justify-between px-1">
                                <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-orange-300">Get started</span>
                                <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Join free in seconds</span>
                            </div>
                            <div className="relative flex justify-center [&>*]:max-w-none [&>*]:w-full">
                                {rightSlot}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <HeroVideo variant="editorial" />

            {/* 2.5 — Sponsored local business */}
            <section className="mx-auto max-w-7xl px-5 sm:px-10 pb-12">
                <AdSpot variant="editorial" placement="landing" />
                <ReferralPromo variant="editorial" />
                <ReferralLeaderboard variant="editorial" />
            </section>

            {/* 3 — Local pros showcase (horizontal snap carousel) */}
            {pros.length > 0 && (
                <section className="py-14 sm:py-20 border-y border-white/10 bg-white/5">
                    <div className="mx-auto max-w-7xl px-5 sm:px-10">
                        <Reveal>
                            <div className="mb-8 flex items-end justify-between">
                                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight">Meet your <span className="text-orange-500">local pros</span></h2>
                                <span className="hidden sm:block font-mono text-xs text-zinc-500">/ BACKGROUND-CHECKED &amp; RATED</span>
                            </div>
                        </Reveal>
                        <div className="flex gap-5 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-3 -mx-1 px-1">
                            {pros.slice(0, 8).map((p, i) => (
                                <div key={p.id} data-testid={`editorial-pro-${i}`} className="snap-start shrink-0 w-[280px] sm:w-[320px] group">
                                    <div className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-zinc-950/50 transition-[border-color,transform] duration-300 group-hover:-translate-y-1.5 group-hover:border-orange-500/50">
                                        <div className="media-overlay relative h-44 overflow-hidden">
                                            <img src={mediaUrl(p.avatar_url)} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 to-transparent" />
                                            <span className="absolute left-3 top-3 rounded-full bg-orange-500 px-2.5 py-0.5 text-[11px] font-bold text-zinc-950">{p.specialty}</span>
                                            {p.verified && <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-zinc-950/70 px-2 py-0.5 text-[10px] font-semibold text-orange-300"><ShieldCheck className="h-3 w-3" /> Verified</span>}
                                            <div className="absolute bottom-3 left-4 right-4">
                                                <p className="font-display text-lg font-bold text-white leading-tight">{p.name}</p>
                                                <p className="flex items-center gap-1 text-[11px] text-zinc-300"><MapPin className="h-3 w-3 text-orange-400" />{(p.city || "").split(",")[0]}</p>
                                            </div>
                                        </div>
                                        <div className="p-4">
                                            <p className="flex items-start gap-1.5 text-xs italic text-zinc-400 leading-relaxed">
                                                <Quote className="h-3.5 w-3.5 shrink-0 text-orange-500/60" />
                                                {(p.bio || "Proud local pro.").slice(0, 92)}…
                                            </p>
                                            <div className="mt-3 flex items-center justify-between border-t border-white/5 pt-3 text-xs">
                                                <span className="flex items-center gap-1 text-amber-300"><Star className="h-3.5 w-3.5 fill-amber-300" />{p.rating} <span className="text-zinc-500">({p.rating_count})</span></span>
                                                <span className="font-mono text-zinc-500">{p.jobs_completed} jobs</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* 4 — Community hire chronicle */}
            {events.length > 0 && (
                <section className="mx-auto max-w-7xl px-5 sm:px-10 py-16 sm:py-20">
                    <Reveal>
                        <div className="mb-8 flex items-end justify-between">
                            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight">Just <span className="text-orange-500">hired</span></h2>
                            <span className="hidden sm:block font-mono text-xs text-zinc-500">/ HAPPENING RIGHT NOW</span>
                        </div>
                    </Reveal>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {events.slice(0, 6).map((e, i) => (
                            <Reveal key={i} delay={i * 0.06}>
                                <div data-testid={`editorial-chronicle-${i}`} className="group flex h-full items-start gap-3 rounded-2xl border border-white/10 bg-zinc-950/50 p-5 transition-[border-color] hover:border-orange-500/40">
                                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-orange-500/10 border border-orange-500/25 font-display font-black text-orange-500">{(e.who || "?")[0]}</span>
                                    <div className="min-w-0">
                                        <p className="text-sm text-zinc-300 leading-relaxed"><span className="font-semibold text-white">{e.who}</span> {e.text?.replace(`${e.who} `, "")}</p>
                                        <p className="mt-1 text-[11px] font-mono uppercase tracking-wide text-zinc-500">{e.city} · {e.mins_ago} min ago</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </section>
            )}

            {/* 5 — Visual trade directory */}
            <section className="mx-auto max-w-7xl px-5 sm:px-10 pb-16 sm:pb-20">
                <Reveal>
                    <h2 className="mb-8 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight">Popular <span className="text-orange-500">around the house</span></h2>
                </Reveal>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {TRADES.map((t, i) => (
                        <Reveal key={t.name} delay={i * 0.05}>
                            <div data-testid={`editorial-trade-${t.name.toLowerCase()}`} className="media-overlay group relative h-40 sm:h-48 overflow-hidden rounded-2xl border border-white/10">
                                <img src={t.img} alt={t.name} className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
                                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent transition-opacity group-hover:from-zinc-950/90" />
                                <div className="absolute bottom-0 left-0 p-4">
                                    <p className="font-display text-lg font-bold text-white">{t.name}</p>
                                    <p className="flex items-center gap-1 text-[11px] text-orange-300 opacity-0 transition-opacity group-hover:opacity-100">Explore pros <ArrowRight className="h-3 w-3" /></p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* 6 — How it works */}
            <section className="mx-auto max-w-7xl px-5 sm:px-10 pb-20">
                <Reveal>
                    <h2 className="mb-8 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight">How it <span className="text-orange-500">works</span></h2>
                </Reveal>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {STEPS.map((s, i) => (
                        <Reveal key={s.n} delay={i * 0.1}>
                            <div className="group relative h-full rounded-3xl border border-white/10 bg-zinc-950/50 p-7 transition-[border-color] hover:border-orange-500/50">
                                <div className="font-mono text-5xl font-black text-orange-500/20 dark:text-white/10 group-hover:text-orange-500/40 dark:group-hover:text-orange-500/30 transition-colors">{s.n}</div>
                                <h3 className="mt-3 font-display text-xl font-bold">{s.title}</h3>
                                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{s.body}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* 7 — Footer */}
            <footer className="border-t border-white/10 px-6 sm:px-10 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
                <ForceLogo size={30} />
                <p className="text-xs text-zinc-500">© {new Date().getFullYear()} FORCE · Let the FORCE assist you.</p>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">LET THE FORCE ASSIST YOU</span>
            </footer>
        </div>
    );
}
